import { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import Navbar from "../Navbar";
import { PRO_LIMITS, formatDuration } from "../../services/proApi";
import { getProBlog, updateProBlog, deleteProBlog } from "../../services/proSystemApi";
import "./Pro.css";

// =====================================================
// EDIT MY OWN PRO BLOG
//
// Reached from My Pro Profile. The creator can change the title, the content
// and the three post settings, and can remove individual images or the video.
//
// WHY THERE IS NO "ADD MORE PHOTOS" HERE
//   Uploading media means enforcing the 10-image and 1-hour-video limits, and
//   those rules live in the composer's upload route. Duplicating them here
//   would create a second place for them to drift out of step. Adding new
//   media therefore means publishing a new blog from Write Blog.
//
// OWNERSHIP
//   This page only opens the editor when the server says `access === "owner"`.
//   That is a convenience — the SERVER re-checks ownership on the save and the
//   delete against the signed token, so a creator cannot edit someone else's
//   blog by typing a different id into the URL.
// =====================================================

function ProEditBlog() {
  const { blogId } = useParams();
  const navigate = useNavigate();

  const [blog, setBlog] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState(null);

  const [title, setTitle] = useState("");
  const [content, setContent] = useState("");
  const [hideComments, setHideComments] = useState(false);
  const [allowShare, setAllowShare] = useState(true);
  const [allowSave, setAllowSave] = useState(true);

  // Marked for removal on save, rather than deleted as you click — so a
  // mis-click is undoable right up until Save.
  const [removeImages, setRemoveImages] = useState([]);
  const [removeVideo, setRemoveVideo] = useState(false);

  const flash = useCallback((msg, type = "info") => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 3800);
  }, []);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const res = await getProBlog(blogId);
      const b = res.blog;

      if (b.locked || b.access !== "owner") {
        setError("You can only edit your own Pro blogs.");
        return;
      }

      setBlog(b);
      setTitle(b.title || "");
      setContent(b.content || "");
      setHideComments(Boolean(b.hideComments));
      setAllowShare(b.allowShare !== false);
      setAllowSave(b.allowSave !== false);
      setRemoveImages([]);
      setRemoveVideo(false);
      setError(null);
    } catch (err) {
      setError(err?.response?.data?.message || "This blog could not be opened");
    } finally {
      setLoading(false);
    }
  }, [blogId]);

  useEffect(() => {
    load();
  }, [load]);

  const toggleImageRemoval = (url) => {
    setRemoveImages((prev) =>
      prev.includes(url) ? prev.filter((u) => u !== url) : [...prev, url]
    );
  };

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);

    try {
      await updateProBlog(blogId, {
        title,
        content,
        hideComments,
        allowShare,
        allowSave,
        removeImages,
        removeVideo,
      });

      flash("Blog updated", "success");
      setTimeout(() => navigate("/pro-profile"), 900);
    } catch (err) {
      flash(err?.response?.data?.message || "Failed to save", "error");
      setSaving(false);
    }
  };

  const remove = async () => {
    const confirmed = window.confirm(
      "Delete this Pro blog permanently?\n\n" +
        "Its comments go with it. Readers who already paid keep their access " +
        "for the rest of their 30 days — deleting a post does not refund anyone.\n\n" +
        "This cannot be undone."
    );

    if (!confirmed) return;

    setSaving(true);

    try {
      const res = await deleteProBlog(blogId);

      // The free-preview pair is the first two blogs in upload order, so
      // deleting one of them promotes the next. Say so rather than let the
      // creator discover it.
      if (res.freePreviewChanged) window.alert(res.note);

      navigate("/pro-profile");
    } catch (err) {
      flash(err?.response?.data?.message || "Failed to delete", "error");
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="pro-page-wrap">
        <Navbar />
        <div className="pro-loading-screen">
          <div className="pro-spinner" />
          <p>Loading…</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="pro-page-wrap">
        <Navbar />
        <div className="pro-container pro-container-narrow">
          <div className="pro-card pro-empty">
            <p>{error}</p>
            <button
              type="button"
              className="pro-btn pro-btn-ghost"
              onClick={() => navigate("/pro-profile")}
            >
              ← My Pro Profile
            </button>
          </div>
        </div>
      </div>
    );
  }

  const titlePct = (title.length / PRO_LIMITS.TITLE_MAX) * 100;
  const contentPct = (content.length / PRO_LIMITS.CONTENT_MAX) * 100;
  const barColor = (pct) => (pct > 90 ? "#ef4444" : pct > 70 ? "#f59e0b" : "#a78bfa");

  const keptImages = (blog.images || []).filter((url) => !removeImages.includes(url));

  return (
    <div className="pro-page-wrap">
      <Navbar />

      {toast && <div className={`pro-toast pro-toast-${toast.type}`}>{toast.msg}</div>}

      <div className="pro-container pro-container-narrow">
        <header className="pro-hero pro-hero-compact">
          <div className="pro-hero-badge">✏️ EDIT PRO BLOG</div>
          <h1 className="pro-hero-title">Edit your blog</h1>
        </header>

        <form className="pro-card pro-form" onSubmit={save}>
          {/* ── TITLE ── */}
          <div className="pro-field">
            <label className="pro-label" htmlFor="pe-title">📝 Title</label>
            <input
              id="pe-title"
              className="pro-input"
              value={title}
              maxLength={PRO_LIMITS.TITLE_MAX}
              onChange={(e) => setTitle(e.target.value)}
              disabled={saving}
            />
            <div className="pro-meter-row">
              <div className="pro-meter">
                <div
                  className="pro-meter-fill"
                  style={{ width: `${titlePct}%`, background: barColor(titlePct) }}
                />
              </div>
              <span className="pro-counter">
                {title.length}/{PRO_LIMITS.TITLE_MAX}
              </span>
            </div>
          </div>

          {/* ── CONTENT ── */}
          <div className="pro-field">
            <label className="pro-label" htmlFor="pe-content">💬 Content</label>
            <textarea
              id="pe-content"
              className="pro-input pro-textarea"
              value={content}
              maxLength={PRO_LIMITS.CONTENT_MAX}
              rows={10}
              onChange={(e) => setContent(e.target.value)}
              disabled={saving}
            />
            <div className="pro-meter-row">
              <div className="pro-meter">
                <div
                  className="pro-meter-fill"
                  style={{ width: `${contentPct}%`, background: barColor(contentPct) }}
                />
              </div>
              <span className="pro-counter">
                {content.length}/{PRO_LIMITS.CONTENT_MAX}
              </span>
            </div>
          </div>

          {/* ── IMAGES — remove only ── */}
          {(blog.images || []).length > 0 && (
            <div className="pro-field">
              <label className="pro-label">
                🖼 Images{" "}
                <span className="pro-optional">
                  ({keptImages.length} kept
                  {removeImages.length > 0 ? `, ${removeImages.length} will be removed` : ""})
                </span>
              </label>

              <div className="pro-gallery">
                {blog.images.map((url, index) => {
                  const marked = removeImages.includes(url);
                  return (
                    <figure
                      className="pro-gallery-item"
                      key={url}
                      style={marked ? { opacity: 0.35 } : undefined}
                    >
                      {/* Screen readers already announce this as an image, so
                          the alt text is just its position in the gallery. */}
                      <img src={url} alt={`${index + 1} of ${blog.images.length}`} />
                      <button
                        type="button"
                        className="pro-gallery-remove"
                        onClick={() => toggleImageRemoval(url)}
                        disabled={saving}
                        title={marked ? "Keep this image" : "Remove this image"}
                        aria-label={marked ? "Keep this image" : "Remove this image"}
                      >
                        {marked ? "↺" : "✕"}
                      </button>
                      <figcaption>{index + 1}</figcaption>
                    </figure>
                  );
                })}
              </div>

              <p className="pro-fineprint">
                To ADD more photos, publish a new blog from Write Blog — the upload
                limits live there.
              </p>
            </div>
          )}

          {/* ── VIDEO — remove only ── */}
          {blog.video && (
            <div className="pro-field">
              <label className="pro-label">🎥 Video</label>

              {removeVideo ? (
                <div className="pro-notice pro-notice-warn pro-notice-inline">
                  <p>The video will be removed when you save.</p>
                  <button
                    type="button"
                    className="pro-btn pro-btn-ghost pro-btn-sm"
                    onClick={() => setRemoveVideo(false)}
                    disabled={saving}
                  >
                    ↺ Keep it
                  </button>
                </div>
              ) : (
                <div className="pro-video-preview">
                  <video src={blog.video} controls preload="metadata" />
                  {blog.videoDuration > 0 && (
                    <p className="pro-counter">Duration {formatDuration(blog.videoDuration)}</p>
                  )}
                  <button
                    type="button"
                    className="pro-btn pro-btn-ghost pro-btn-sm"
                    onClick={() => setRemoveVideo(true)}
                    disabled={saving}
                  >
                    ✕ Remove video
                  </button>
                </div>
              )}
            </div>
          )}

          {/* ── POST SETTINGS ── */}
          <div className="pro-field">
            <label className="pro-label">⚙ Post Settings</label>
            <div className="pro-toggle-row">
              <label className="pro-toggle">
                <input
                  type="checkbox"
                  checked={hideComments}
                  onChange={(e) => setHideComments(e.target.checked)}
                  disabled={saving}
                />
                <span>Hide comments</span>
              </label>

              <label className="pro-toggle">
                <input
                  type="checkbox"
                  checked={!allowShare}
                  onChange={(e) => setAllowShare(!e.target.checked)}
                  disabled={saving}
                />
                <span>Turn off sharing</span>
              </label>

              <label className="pro-toggle">
                <input
                  type="checkbox"
                  checked={!allowSave}
                  onChange={(e) => setAllowSave(!e.target.checked)}
                  disabled={saving}
                />
                <span>Turn off saving</span>
              </label>
            </div>
          </div>

          <div className="pro-form-actions">
            <button
              type="button"
              className="pro-btn pro-btn-ghost"
              onClick={() => navigate("/pro-profile")}
              disabled={saving}
            >
              Cancel
            </button>
            <button type="submit" className="pro-btn pro-btn-primary" disabled={saving}>
              {saving ? "Saving…" : "Save changes"}
            </button>
          </div>
        </form>

        {/* ── DELETE — kept apart from the form so it cannot be hit by
            pressing Enter in a text field. ── */}
        <section className="pro-card pro-notice pro-notice-warn">
          <strong>Delete this blog</strong>
          <p>
            Permanent. Its comments are deleted too. Readers who already paid keep
            their access for the rest of their 30 days — deleting a post does not
            refund anyone.
          </p>
          <button type="button" className="pro-btn" onClick={remove} disabled={saving}>
            🗑 Delete blog
          </button>
        </section>
      </div>
    </div>
  );
}

export default ProEditBlog;
